import { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import { Checkbox } from "antd";
import { useSelector } from "react-redux";
import { trainingAPI } from "@api/TrainingAPI";
import { get_metric_info_from_identifier, getDropdownKey } from "@utils/chart_utils";
import BellCurveChart from "@components/Charts/BellCurveChart";
import { mapDataForMultiCognitionBellCurve } from "@mappers/model/cognitionBellCurveMapper";
import { LoadingComponent } from "@components/Common/LoadingComponent";

type Props = {
  statMetrics: any[];
  startDate: string;
  endDate: string;
  playerId?: string;
  position?: any;
  player_group?: any;
  session?: any;
  playerName?: string;
};

const CognitionBellCurveChart = ({
  statMetrics,
  startDate,
  endDate,
  playerId,
  position,
  player_group,
  session,
  playerName,
}: Props) => {
  const { all_metric_types } = useSelector(({ team }: any) => team);
  const [seriesData, setSeriesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCurve, setShowCurve] = useState(true);
  const [checkboxLeft, setCheckboxLeft] = useState(35);

  const allMetrics: any[] = Object.values(all_metric_types as Record<string, any[]>).flat();

  useEffect(() => {
    const validMetrics = statMetrics.filter(Boolean);
    if (!validMetrics.length) {
      setSeriesData([]);
      return;
    }

    const fetchAll = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          validMetrics.map(async (metricIdentifier: string) => {
            const { metric_id, metric_stat } = get_metric_info_from_identifier(metricIdentifier);
            const metric = allMetrics.find((m: any) => getDropdownKey(m, "metricId") === metric_id);
            const data = await trainingAPI.getTestingData({
              start_date: startDate,
              end_date: endDate,
              metric_type: "cognition",
              stats: metric_stat,
              metric_id,
              player_id: playerId,
              pos_filter: position,
              player_group,
              sessions: session,
              do_not_aggregate: true,
              skip_color: true,
            });
            return { metric, data, stat: metric_stat };
          })
        );
        setSeriesData(results.filter(({ metric }) => metric != null));
      } catch {
        setSeriesData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [
    statMetrics.filter(Boolean).join(","),
    startDate,
    endDate,
    playerId,
  ]);

  const options = useMemo(
    () => mapDataForMultiCognitionBellCurve({ seriesData, showCurve, title: playerName }),
    [seriesData, showCurve, playerName]
  );

  const handleChartCreated = (chart: Highcharts.Chart) => {
    if (chart?.plotLeft) {
      const newLeft = chart.plotLeft - 6;
      setCheckboxLeft((prev) => (prev === newLeft ? prev : newLeft));
    }
  };

  return (
    <LoadingComponent loading={loading} title="Loading chart data...">
      <div style={{ position: "relative" }}>
        <BellCurveChart options={options} onChartCreated={handleChartCreated} />
        {options && (
          <div style={{
            position: "absolute",
            top: "10px",
            left: `${checkboxLeft}px`,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            borderRadius: 3,
            padding: "2px 6px",
            zIndex: 10,
          }}>
            <Checkbox
              checked={showCurve}
              onChange={(e) => setShowCurve(e.target.checked)}
              style={{ color: "#888888" }}
            >
              Show Curve
            </Checkbox>
          </div>
        )}
      </div>
    </LoadingComponent>
  );
};

export default CognitionBellCurveChart;
